import { CartItem, Item } from "./api";

const CART_STORAGE_KEY = "namma_nesavu_nool_cart";

export const cartService = {
  // Get all cart items
  getCartItems: (): CartItem[] => {
    try {
      const cartData = localStorage.getItem(CART_STORAGE_KEY);
      return cartData ? JSON.parse(cartData) : [];
    } catch (error) {
      console.error("Error reading cart from localStorage:", error);
      return [];
    }
  },

  // Add item to cart
  addToCart: (
    product: Item,
    quantity: number = 1,
    selectedColor?: string,
    selectedSize?: string
  ): void => {
    const cartItems = cartService.getCartItems();
    const existingItemIndex = cartItems.findIndex(
      (item) =>
        item.productId === product.id &&
        item.selectedColor === selectedColor &&
        item.selectedSize === selectedSize
    );

    if (existingItemIndex >= 0) {
      // Update quantity if item already exists
      cartItems[existingItemIndex].quantity += quantity;
    } else {
      // Add new item
      cartItems.push({
        productId: product.id,
        product: product,
        quantity: quantity,
        selectedColor: selectedColor,
        selectedSize: selectedSize || product.size,
      });
    }

    cartService.saveCartItems(cartItems);
  },

  // Remove item from cart
  removeFromCart: (
    productId: string,
    selectedColor?: string,
    selectedSize?: string
  ): void => {
    const cartItems = cartService.getCartItems();
    const filteredItems = cartItems.filter(
      (item) =>
        !(
          item.productId === productId &&
          item.selectedColor === selectedColor &&
          item.selectedSize === selectedSize
        )
    );
    cartService.saveCartItems(filteredItems);
  },

  // Update item quantity
  updateQuantity: (
    productId: string,
    quantity: number,
    selectedColor?: string,
    selectedSize?: string
  ): void => {
    const cartItems = cartService.getCartItems();
    const itemIndex = cartItems.findIndex(
      (item) =>
        item.productId === productId &&
        item.selectedColor === selectedColor &&
        item.selectedSize === selectedSize
    );

    if (itemIndex >= 0) {
      if (quantity <= 0) {
        // Remove item if quantity is 0 or less
        cartItems.splice(itemIndex, 1);
      } else {
        cartItems[itemIndex].quantity = quantity;
      }
      cartService.saveCartItems(cartItems);
    }
  },

  // Clear entire cart
  clearCart: (): void => {
    localStorage.removeItem(CART_STORAGE_KEY);
  },

  // Get cart count (total items)
  getCartCount: (): number => {
    const cartItems = cartService.getCartItems();
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  },

  // Get cart total price
  getCartTotal: (): number => {
    const cartItems = cartService.getCartItems();
    return cartItems.reduce((total, item) => {
      return total + item.product.price * item.quantity;
    }, 0);
  },

  // Save cart items to localStorage
  saveCartItems: (items: CartItem[]): void => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items));
      // Dispatch custom event for cart updates
      window.dispatchEvent(new CustomEvent("cartUpdated"));
    } catch (error) {
      console.error("Error saving cart to localStorage:", error);
    }
  },

  // Calculate discount percentage
  calculateDiscount: (price: number, mrp?: number): number => {
    if (!mrp || mrp <= price) return 0;
    return Math.round(((mrp - price) / mrp) * 100);
  },
};
